const SERIF = "'Bodoni Moda', Georgia, serif";
const MONO  = "'IBM Plex Mono', monospace";

const Section = ({ title, children }) => (
  <section className="space-y-4">
    <h2 className="text-xl font-semibold text-foreground border-b border-border pb-3">{title}</h2>
    <div className="space-y-3 text-muted-foreground leading-relaxed text-sm">{children}</div>
  </section>
);

const CookieRow = ({ name, purpose, duration, type }) => (
  <tr>
    <td className="px-3 py-2 font-mono text-xs text-foreground align-top">{name}</td>
    <td className="px-3 py-2 text-xs align-top">{purpose}</td>
    <td className="px-3 py-2 text-xs align-top">{duration}</td>
    <td className="px-3 py-2 text-xs align-top">{type}</td>
  </tr>
);

const CategoryBlock = ({ badge, badgeColor, label, children }) => (
  <div className={`border p-4 space-y-3 ${badgeColor === 'green' ? 'border-green-200 bg-green-50' : 'border-border bg-white'}`}>
    <div className="flex items-center gap-2">
      <span className={`text-xs px-2 py-0.5 font-medium ${
        badgeColor === 'green' ? 'bg-green-600 text-white' :
        badgeColor === 'dark'  ? 'bg-foreground text-background' :
        badgeColor === 'amber' ? 'bg-amber-600 text-white' :
        'border border-border text-foreground'
      }`}>{badge}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
    {children}
  </div>
);

const CookieTable = ({ rows, greenBorder = false }) => (
  <div className="overflow-x-auto">
    <table className={`w-full text-xs border mt-2 ${greenBorder ? 'border-green-200' : 'border-border'}`}>
      <thead>
        <tr className={greenBorder ? 'bg-green-100' : 'bg-secondary'}>
          <th className={`text-left px-3 py-2 border-b font-semibold ${greenBorder ? 'border-green-200 text-green-900' : 'border-border text-foreground'}`}>Cookie / Key</th>
          <th className={`text-left px-3 py-2 border-b font-semibold ${greenBorder ? 'border-green-200 text-green-900' : 'border-border text-foreground'}`}>Purpose</th>
          <th className={`text-left px-3 py-2 border-b font-semibold ${greenBorder ? 'border-green-200 text-green-900' : 'border-border text-foreground'}`}>Duration</th>
          <th className={`text-left px-3 py-2 border-b font-semibold ${greenBorder ? 'border-green-200 text-green-900' : 'border-border text-foreground'}`}>Type</th>
        </tr>
      </thead>
      <tbody className={`divide-y ${greenBorder ? 'divide-green-200' : 'divide-border'}`}>
        {rows.map(r => <CookieRow key={r[0]} name={r[0]} purpose={r[1]} duration={r[2]} type={r[3]} />)}
      </tbody>
    </table>
  </div>
);

const Cookies = () => {
  const lastUpdated = 'February 2026';

  return (
    <div className="min-h-screen bg-white py-20 px-6 lg:px-20">
      <div className="max-w-3xl mx-auto">

        <div className="mb-14">
          <span className="text-xs uppercase tracking-widest text-muted-foreground" style={{ fontFamily: MONO }}>
            Legal · Cookie Policy
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold mt-4 mb-3 tracking-tight" style={{ fontFamily: SERIF }}>
            Cookie Policy
          </h1>
          <p className="text-sm text-muted-foreground" style={{ fontFamily: MONO }}>
            Last updated: {lastUpdated}
          </p>
        </div>

        <div className="space-y-12">

          <Section title="1. What Are Cookies">
            <p>
              Cookies are small text files placed on your device when you visit a website. They allow FashionSpyder
              to recognise you across sessions, measure how the platform is used, and deliver more relevant
              content and advertising.
            </p>
            <p>
              We operate a <strong className="text-foreground">consent banner</strong> on first visit.
              You may accept all cookies or essential cookies only. You can update your choice at any time by
              clearing your browser cookies and reloading the site.
            </p>
          </Section>

          <Section title="2. Categories of Cookies We Use">

            {/* Essential */}
            <CategoryBlock badge="ESSENTIAL" badgeColor="green" label="Always active — cannot be disabled">
              <p>
                Strictly necessary for FashionSpyder to function. These enable login, session security, and
                your consent preference itself. They cannot be disabled without breaking the platform.
              </p>
              <CookieTable greenBorder rows={[
                ['session_token',    'Maintains your authenticated HR login session',              '7 days',  'httpOnly, Secure'],
                ['fs_cookie_consent','Stores your cookie consent preference',                      '1 year',  'localStorage'],
                ['csrf_token',       'Protects against cross-site request forgery attacks',        'Session', 'httpOnly, Secure'],
              ]} />
            </CategoryBlock>

            {/* Functional */}
            <CategoryBlock badge="FUNCTIONAL" badgeColor="dark" label="Active with your consent">
              <p>
                These cookies remember your in-session choices to make the platform more convenient.
                They do not track you across sessions or share data with third parties.
              </p>
              <CookieTable rows={[
                ['recentlyViewed',   'Stores the last 10 candidate profiles you browsed (gallery strip)', 'Session', 'sessionStorage'],
                ['gallery_filters',  'Remembers your last applied filter set in the gallery',             'Session', 'sessionStorage'],
                ['fs_session_token', 'Frontend copy of your session token for authenticated API calls',   '7 days',  'localStorage'],
              ]} />
            </CategoryBlock>

            {/* Analytics */}
            <CategoryBlock badge="ANALYTICS" badgeColor="" label="Active with your consent">
              <p>
                We use <strong className="text-foreground">Google Analytics 4 (GA4)</strong> to understand
                how visitors use FashionSpyder — which pages are visited, how long users stay, and what
                features are most used. This data is aggregated and anonymised: we do not share individual
                user data with Google for advertising purposes. IP addresses are anonymised.
              </p>
              <CookieTable rows={[
                ['_ga',     'Google Analytics 4 — unique client identifier',       '2 years',  'Persistent'],
                ['_ga_*',   'GA4 — session and engagement tracking per property',  '2 years',  'Persistent'],
                ['_gid',    'GA4 — daily session tracking',                        '24 hours', 'Persistent'],
                ['_gac_*',  'GA4 — campaign attribution and conversion tracking',  '90 days',  'Persistent'],
              ]} />
              <p>
                Google Analytics is configured with IP anonymisation enabled. For Google's privacy policy, see{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer"
                  className="underline underline-offset-2 text-foreground">
                  policies.google.com/privacy
                </a>.
                To opt out of GA4 tracking, install the{' '}
                <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer"
                  className="underline underline-offset-2 text-foreground">
                  Google Analytics Opt-out Browser Add-on
                </a>.
              </p>
            </CategoryBlock>

            {/* Marketing */}
            <CategoryBlock badge="MARKETING" badgeColor="amber" label="Active with your consent">
              <p>
                We use marketing pixels to measure the effectiveness of our campaigns and to reach relevant
                audiences on social platforms. These pixels are loaded <strong className="text-foreground">only
                if you accept marketing cookies</strong> via our consent banner.
              </p>

              <p className="font-medium text-foreground mt-2">Meta (Facebook) Pixel</p>
              <p>
                The Meta Pixel allows us to measure conversions from Meta ads and build audiences of HR
                professionals in fashion. Meta may use this data to show FashionSpyder ads on Facebook
                and Instagram. Meta's data policy applies:{' '}
                <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer"
                  className="underline underline-offset-2 text-foreground">
                  facebook.com/privacy/policy
                </a>.
              </p>
              <CookieTable rows={[
                ['_fbp',  'Facebook Pixel — browser identifier for conversion tracking',  '90 days', 'Persistent'],
                ['fr',    'Facebook — cross-site tracking and ad personalisation',         '90 days', 'Persistent'],
              ]} />

              <p className="font-medium text-foreground mt-3">Pinterest Tag</p>
              <p>
                We use the Pinterest Tag to measure visits from Pinterest campaigns and to reach fashion
                professionals who use Pinterest for trend research and creative inspiration — consistent with
                our Trend Manifest feature which draws on Pinterest Predicts data. Pinterest's privacy
                policy:{' '}
                <a href="https://policy.pinterest.com/en/privacy-policy" target="_blank" rel="noopener noreferrer"
                  className="underline underline-offset-2 text-foreground">
                  policy.pinterest.com/privacy-policy
                </a>.
              </p>
              <CookieTable rows={[
                ['_pin_unauth',       'Pinterest Tag — anonymous visitor tracking',            '1 year',  'Persistent'],
                ['_pinterest_ct_ua',  'Pinterest — user activity and conversion tracking',     '1 year',  'Persistent'],
                ['_pinterest_sess',   'Pinterest — session cookie for logged-in Pinterest users', 'Session', 'Session'],
              ]} />

              <p className="text-xs text-muted-foreground mt-2">
                To opt out of interest-based advertising across platforms, visit{' '}
                <a href="https://www.youronlinechoices.eu" target="_blank" rel="noopener noreferrer"
                  className="underline underline-offset-2 text-foreground">
                  youronlinechoices.eu
                </a>{' '}(EU) or{' '}
                <a href="https://optout.aboutads.info" target="_blank" rel="noopener noreferrer"
                  className="underline underline-offset-2 text-foreground">
                  optout.aboutads.info
                </a>{' '}(US).
              </p>
            </CategoryBlock>

          </Section>

          <Section title="3. Consent & Managing Your Preferences">
            <p>You have full control over which cookie categories you allow:</p>
            <ul className="list-disc pl-4 space-y-2">
              <li>
                <strong className="text-foreground">Consent banner:</strong> On your first visit, choose
                "Accept All" (all categories) or "Essential Only" (essential cookies only). Marketing and
                analytics scripts are not loaded until you consent.
              </li>
              <li>
                <strong className="text-foreground">Reset your choice:</strong> Clear your browser cookies
                and site data — the consent banner will reappear on your next visit.
              </li>
              <li>
                <strong className="text-foreground">Browser settings:</strong> You can block, delete, or
                manage cookies at browser level. Note that blocking essential cookies will prevent login.
              </li>
              <li>
                <strong className="text-foreground">Platform opt-outs:</strong> You can opt out of Google
                Analytics, Meta, and Pinterest tracking directly from their platforms or via your platform
                account privacy settings.
              </li>
            </ul>
          </Section>

          <Section title="4. Data Transfers">
            <p>
              Google Analytics, Meta Pixel, and Pinterest Tag may transfer data to servers outside the
              European Economic Area (EEA). Google and Meta have implemented Standard Contractual Clauses
              (SCCs) as safeguards for such transfers under GDPR Article 46. Pinterest maintains a
              representative in the EU under GDPR Article 27.
            </p>
            <p>
              We have entered into Data Processing Agreements (DPAs) with each of these third-party
              analytics providers.
            </p>
          </Section>

          <Section title="5. Changes to This Policy">
            <p>
              We review this Cookie Policy when we add or remove tracking technologies, or as applicable
              law changes. The "Last updated" date above reflects the most recent revision. Material changes
              will be communicated via the consent banner on your next visit.
            </p>
          </Section>

          <Section title="6. Contact">
            <p>
              Questions about our use of cookies or to exercise your rights under GDPR:{' '}
              <a href="mailto:privacy@fashionspyder.com" className="text-foreground underline underline-offset-2">
                privacy@fashionspyder.com
              </a>
            </p>
          </Section>

        </div>
      </div>
    </div>
  );
};

export default Cookies;
